/**
 * Schemas Zod do contrato do callable `sendTransactionalEmail` e dos dados
 * (input variables) de cada template. Backend renderiza o template React
 * Email correspondente passando esses dados validados.
 *
 * Convenções:
 * - `EmailTemplateId` enum vem de `enums.ts` (fonte única).
 * - `EmailTemplateData` é um discriminated union — cada variante carrega o
 *   `templateId` literal e os campos específicos. Isso garante que o
 *   compilador relacione 1:1 template ↔ dados.
 * - Money sempre em centavos inteiros + ISO currency code (alinhado com o
 *   resto do billing).
 * - Datas que aparecem no corpo do email são ISO 8601 strings — formatação
 *   de exibição é responsabilidade do template (i18n + timezone).
 */
import { z } from 'zod';
import {
  ALL_COUNTRIES,
  ALL_CURRENCIES,
  ALL_EMAIL_EVENT_TYPES,
  ALL_EMAIL_LOCALES,
  EmailTemplateId,
} from './enums';
import { AlertKindSchema, AlertSeveritySchema } from './aggregations';
import { AdminNotificationEventSchema } from './adminNotifications';
import { UsageAlertKindSchema } from './usagePattern';
import { ReportDocumentSchema } from './reports';
import { EquipmentEmailStageSchema, EquipmentKindSchema } from './equipment';

const nonEmpty = z.string().min(1);
const emailAddr = z.email();
const isoDate = z.iso.datetime();

export const EmailLocaleSchema = z.enum(ALL_EMAIL_LOCALES);
export const EmailCountrySchema = z.enum(ALL_COUNTRIES);

/**
 * Recipient: endereço de email + nome opcional para `To` header. Validado
 * em todo template — usado como `personalizations[].to[]` no SendGrid.
 */
export const EmailRecipientSchema = z.object({
  email: emailAddr,
  name: nonEmpty.optional(),
});
export type EmailRecipient = z.infer<typeof EmailRecipientSchema>;

const baseTenantContext = z.object({
  tenantId: nonEmpty,
  tenantName: nonEmpty,
});

const baseUserContext = z.object({
  userId: nonEmpty,
  userName: nonEmpty.optional(),
});

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

const OnboardingWelcomeData = z.object({
  templateId: z.literal(EmailTemplateId.ONBOARDING_WELCOME),
  ...baseUserContext.shape,
  ...baseTenantContext.shape,
  appUrl: z.url(),
});

/**
 * #679 F1 — boas-vindas da venda assistida. `setPasswordUrl` é o link de
 * definição de senha do Firebase Auth, gerado NO ENVIO (expira em ~1 h).
 */
const SaleWelcomeData = z.object({
  templateId: z.literal(EmailTemplateId.SALE_WELCOME),
  ...baseUserContext.shape,
  ...baseTenantContext.shape,
  setPasswordUrl: z.url(),
  appUrl: z.url(),
  /**
   * #967 (baixo, 2026-10-13 22:20 +0200): `true` = quem pagou foi o parceiro
   * RESELLER (`bill_to`). O texto não diz ao cliente "recebemos o pagamento" —
   * ele não pagou nada. Ausente = `false` (payload anterior).
   */
  paidByPartner: z.boolean().optional(),
});

/**
 * #1047 — e-mail de equipamentos ao OWNER (impressora obrigatória; leitor,
 * tags NFC e balança se usar o módulo). O cliente RESPONDE este e-mail: o
 * Reply-To e o rodapé são `replyToEmail` (`equipmentContactEmail`).
 * `trial` = o tenant está (ou esteve, no lembrete) num período grátis — muda
 * o texto, não o envio. `partnerName` presente = tenant de parceiro: o texto
 * diz que é o parceiro quem pesquisa e manda o link.
 */
const OnboardingEquipmentData = z.object({
  templateId: z.literal(EmailTemplateId.ONBOARDING_EQUIPMENT),
  ...baseUserContext.shape,
  ...baseTenantContext.shape,
  stage: EquipmentEmailStageSchema,
  trial: z.boolean(),
  requiredEquipment: z.array(EquipmentKindSchema).min(1),
  moduleEquipment: z.array(EquipmentKindSchema),
  replyToEmail: emailAddr,
  partnerName: nonEmpty.optional(),
  compatiblePrintersUrl: z.url(),
});

/**
 * #942 — código de 6 dígitos do OTP de e-mail da assinatura self-service do
 * iOS (`requestEmailOtp`). O código vai em claro só no e-mail; o servidor
 * guarda o hash em `emailOtps/{sha256(email)}`. `expiresInMinutes` vem de
 * `EMAIL_OTP_TTL_MS` (`selfServiceIap.ts`).
 */
const EmailOtpCodeData = z.object({
  templateId: z.literal(EmailTemplateId.EMAIL_OTP_CODE),
  ...baseUserContext.shape,
  code: z.string().regex(/^[0-9]{6}$/),
  expiresInMinutes: z.number().int().positive(),
});

const OnboardingTenantInviteData = z.object({
  templateId: z.literal(EmailTemplateId.ONBOARDING_TENANT_INVITE),
  ...baseTenantContext.shape,
  invitedByName: nonEmpty,
  invitedByEmail: emailAddr,
  acceptUrl: z.url(),
  expiresAt: isoDate,
});

// ---------------------------------------------------------------------------
// Billing — Sprint 16. Cobrança recorrente: 5 templates novos +
// suspend/reactivate mantidos. Cada template carrega o invoice/payment
// snapshot necessário para renderizar copy + CTA. Valores em CENTAVOS
// (menor unidade da moeda, CR-01), como em Invoice/Payment/Subscription: o
// template divide por 100 em `formatAmount` (`_layout/formatHelpers.ts`).
// Corrigido em 2026-10-10 (#798): dizia "decimal (unidade da moeda)".
// ---------------------------------------------------------------------------

const CurrencyShortSchema = z.enum(ALL_CURRENCIES);

/**
 * Snapshot mínimo de uma fatura embarcado no payload do email — evita
 * que o template precise ler Firestore. Valores frozen no envio.
 */
const InvoiceSnapshot = z.object({
  invoiceId: nonEmpty,
  invoiceNumber: nonEmpty,
  currency: CurrencyShortSchema,
  totalAmount: z.number().nonnegative(),
  amountDue: z.number().nonnegative(),
  issuedAt: isoDate,
  dueAt: isoDate,
  periodStart: isoDate,
  periodEnd: isoDate,
  pdfUrl: z.url().nullable(),
  /**
   * Botão "pagar agora". `null` desde a #967 (M2, 2026-10-13): cliente pago
   * pelo parceiro RESELLER (`bill_to`) sem link da cobrança — o parceiro não
   * tem login no app, e o fallback `/account/invoices` o levaria a uma tela
   * fechada. Com `null` o template omite o botão.
   */
  payNowUrl: z.url().nullable(),
});

const InvoiceCreatedData = z.object({
  templateId: z.literal(EmailTemplateId.INVOICE_CREATED),
  ...baseTenantContext.shape,
  invoice: InvoiceSnapshot,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const InvoiceDueSoonData = z.object({
  templateId: z.literal(EmailTemplateId.INVOICE_DUE_SOON),
  ...baseTenantContext.shape,
  invoice: InvoiceSnapshot,
  daysUntilDue: z.number().int().positive(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const InvoiceOverdueData = z.object({
  templateId: z.literal(EmailTemplateId.INVOICE_OVERDUE),
  ...baseTenantContext.shape,
  invoice: InvoiceSnapshot,
  daysOverdue: z.number().int().positive(),
  suspendInDays: z.number().int().nonnegative(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const PaymentSnapshot = z.object({
  paymentId: nonEmpty,
  invoiceId: nonEmpty,
  invoiceNumber: nonEmpty,
  currency: CurrencyShortSchema,
  amount: z.number().nonnegative(),
  paidAt: isoDate.nullable(),
  failedAt: isoDate.nullable(),
  failedReason: z.string().nullable(),
  receiptUrl: z.url().nullable(),
});

const PaymentSucceededData = z.object({
  templateId: z.literal(EmailTemplateId.PAYMENT_SUCCEEDED),
  ...baseTenantContext.shape,
  payment: PaymentSnapshot,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const PaymentFailedData = z.object({
  templateId: z.literal(EmailTemplateId.PAYMENT_FAILED),
  ...baseTenantContext.shape,
  payment: PaymentSnapshot,
  /** `null` = sem botão (pagador RESELLER sem login no app — #967, M2). */
  retryUrl: z.url().nullable(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

/**
 * Cobrança recorrente pediu ação do cliente (3DS/SCA). Diferente de
 * PAYMENT_FAILED: ainda não é inadimplência — o cliente só precisa concluir
 * a autenticação. `actionUrl` é o `hosted_invoice_url` do Stripe.
 */
const PaymentActionRequiredData = z.object({
  templateId: z.literal(EmailTemplateId.PAYMENT_ACTION_REQUIRED),
  ...baseTenantContext.shape,
  invoice: InvoiceSnapshot,
  actionUrl: z.url(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const BillingSuspendedData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_SUSPENDED),
  ...baseTenantContext.shape,
  suspendedAt: isoDate,
  reason: nonEmpty,
  /** `null` = sem botão (pagador RESELLER sem login no app — #967, M2). */
  reactivateUrl: z.url().nullable(),
});

const BillingReactivatedData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_REACTIVATED),
  ...baseTenantContext.shape,
  reactivatedAt: isoDate,
});

// ---------------------------------------------------------------------------
// Account / Security
// ---------------------------------------------------------------------------

const AccountEmailChangedData = z.object({
  templateId: z.literal(EmailTemplateId.ACCOUNT_EMAIL_CHANGED),
  ...baseUserContext.shape,
  previousEmail: emailAddr,
  newEmail: emailAddr,
  changedAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const AccountPasswordChangedData = z.object({
  templateId: z.literal(EmailTemplateId.ACCOUNT_PASSWORD_CHANGED),
  ...baseUserContext.shape,
  changedAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
  /**
   * Quem trocou a senha, quando NÃO foi o próprio titular (#214). O
   * `setMemberPassword` permite que OWNER/MANAGER troquem a senha de um
   * membro — nesse caso o aviso precisa dizer quem foi, senão o texto
   * "não foi você? avise o suporte" transforma uma operação legítima em
   * alarme de invasão. Ausente = o próprio titular trocou.
   */
  changedByName: nonEmpty.optional(),
  /** Empresa em cujo contexto a troca aconteceu — só junto de `changedByName`. */
  tenantName: nonEmpty.optional(),
});

const AccountNewDeviceLoginData = z.object({
  templateId: z.literal(EmailTemplateId.ACCOUNT_NEW_DEVICE_LOGIN),
  ...baseUserContext.shape,
  loggedInAt: isoDate,
  deviceLabel: nonEmpty,
  approxLocation: nonEmpty.optional(),
  ipAddress: nonEmpty.optional(),
  reviewSessionsUrl: z.url(),
});

// ---------------------------------------------------------------------------
// Operacional (avisa Owner)
// ---------------------------------------------------------------------------

const OpsTenantUserInvitedData = z.object({
  templateId: z.literal(EmailTemplateId.OPS_TENANT_USER_INVITED),
  ...baseTenantContext.shape,
  invitedUserName: nonEmpty.optional(),
  invitedUserEmail: emailAddr,
  invitedByName: nonEmpty,
  invitedAt: isoDate,
});

const OpsSuspectedDataResetData = z.object({
  templateId: z.literal(EmailTemplateId.OPS_SUSPECTED_DATA_RESET),
  ...baseTenantContext.shape,
  detectedAt: isoDate,
  evidence: nonEmpty,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// ---------------------------------------------------------------------------
// Lifecycle do tenant (Sprint 19) — encerramento e restauração.
// `tenant_deleted` é enviado a TODOS os OWNERs do tenant quando super-admin
// encerra a organização via `requestTenantDeletion`. Carrega prazo para
// solicitar restauração e link de help center. `tenant_restored` é o
// follow-up quando `restoreTenant` é executado dentro do prazo (raro,
// coordenado com support) — lembra que o status final é SUSPENDED_ADMIN
// e o owner precisa reativar a subscription via paywall.
// ---------------------------------------------------------------------------

const LifecycleTenantDeletedData = z.object({
  templateId: z.literal(EmailTemplateId.LIFECYCLE_TENANT_DELETED),
  ...baseTenantContext.shape,
  deletedAt: isoDate,
  /**
   * Motivo livre digitado pelo super-admin no callable. Já chega
   * sanitizado (trim + length cap) — exibimos como-está no email.
   * Opcional pois nem toda operação inclui motivo público.
   */
  deletionReason: z.string().optional(),
  /**
   * Limite para solicitar restauração via support — após esse momento,
   * Sprint 13 (retenção LGPD/GDPR) procede com hard-delete.
   * Calculado pelo backend = `deletedAt + 90 dias`.
   */
  restoreDeadlineAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
  /**
   * Página de help center do locale do destinatário com instruções de
   * restauração e prazos. Backend monta a URL conforme locale.
   */
  helpCenterUrl: z.url(),
});

const LifecycleTenantRestoredData = z.object({
  templateId: z.literal(EmailTemplateId.LIFECYCLE_TENANT_RESTORED),
  ...baseTenantContext.shape,
  restoredAt: isoDate,
  /**
   * Página de billing do app web onde o owner pode reativar a
   * subscription via paywall — restore deixa o tenant em
   * SUSPENDED_ADMIN e a subscription cancelada.
   */
  nextStepsUrl: z.url(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// ---------------------------------------------------------------------------
// Privacy / LGPD — Sprint 13. Email enviado quando o ZIP de export do
// titular está pronto. Carrega signed URL com validade de 7 dias e
// quantidades por categoria de dado para o usuário saber o que está
// dentro antes de baixar.
// ---------------------------------------------------------------------------

const PrivacyDataExportReadyData = z.object({
  templateId: z.literal(EmailTemplateId.PRIVACY_DATA_EXPORT_READY),
  ...baseUserContext.shape,
  /** URL assinada do ZIP no Cloud Storage, válida até `expiresAt`. */
  downloadUrl: z.url(),
  /** Momento exato em que o link expira (ISO 8601). */
  expiresAt: isoDate,
  /** Tamanho do arquivo em bytes — exibido no template. */
  sizeBytes: z.number().int().nonnegative(),
  /** Contagens por categoria — para transparência e validação. */
  recordCounts: z.object({
    profile: z.number().int().nonnegative(),
    memberships: z.number().int().nonnegative(),
    labels: z.number().int().nonnegative(),
    audit: z.number().int().nonnegative(),
  }),
  /**
   * Marca quando algum cap de coleta foi atingido (ex.: >5000 etiquetas).
   * O template avisa o usuário caso `true`.
   */
  truncated: z.boolean(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// ---------------------------------------------------------------------------
// Privacy / LGPD — Sprint 13 PR C. Soft-delete da conta do titular,
// restauração e link OOB para confirmação de encerramento self-service de
// uma organização (OWNER).
// ---------------------------------------------------------------------------

const PrivacyAccountDeletionRequestedData = z.object({
  templateId: z.literal(EmailTemplateId.PRIVACY_ACCOUNT_DELETION_REQUESTED),
  ...baseUserContext.shape,
  /** Momento em que o user foi marcado para soft-delete. */
  requestedAt: isoDate,
  /** `requestedAt + 30 dias` — após isso o sweep apaga fisicamente. */
  hardDeleteAt: isoDate,
  /**
   * Página "Contato" do site no idioma do e-mail (#634, 2026-10-29): não há
   * restore self-service — quem quer voltar fala com o comercial, e a volta é
   * a venda nova do CRM. Substitui o antigo `restoreUrl` (→ `/privacy`, que
   * nunca teve o botão prometido — #549).
   */
  salesContactUrl: z.url(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const PrivacyAccountRestoredData = z.object({
  templateId: z.literal(EmailTemplateId.PRIVACY_ACCOUNT_RESTORED),
  ...baseUserContext.shape,
  /** Momento da restauração. */
  restoredAt: isoDate,
  /** Página do app para login pós-restore. */
  appUrl: z.url(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// ---------------------------------------------------------------------------
// Pedido de exclusão de conta pelo app ("Excluir minha conta",
// `deactivateMyAccount`). Fluxo irmão do LGPD acima, e não o mesmo: o pedido é
// de EXCLUSÃO, mas o primeiro passo é só cortar o acesso — Auth desligado e
// marcador `accessDisabledBy: 'SELF'` em `users/{uid}`; quando quem pede é dono,
// a equipe dos estabelecimentos dele cai junto (`OWNER_REQUEST`). Os dados não
// são apagados automaticamente: a exclusão definitiva é feita pela nossa equipe,
// avisada por `OPS_ACCOUNT_DELETION_REQUESTED`, e até lá o suporte pode
// reativar. O e-mail é "Recebemos seu pedido de exclusão", não "acesso
// desativado".
// Corrigido em 2026-10-15 12:11 +0200 (#937, achado da #801): este comentário
// dizia "autodesativação reversível … NADA é apagado — só o login para de
// funcionar", a leitura que 7cfba15d5 (2026-09-17) abandonou.
// ---------------------------------------------------------------------------

const AccountDeactivatedData = z.object({
  templateId: z.literal(EmailTemplateId.ACCOUNT_DEACTIVATED),
  ...baseUserContext.shape,
  /** Momento em que o acesso foi desligado. */
  deactivatedAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
  /**
   * #942 — presente quando o aviso sai porque alguém tentou criar conta ou
   * entrar de novo com este e-mail (`notifyExcludedAccountAttempt`), e não
   * na hora da exclusão. O template acrescenta a frase da tentativa.
   */
  attemptedAt: isoDate.optional(),
});

const OrphanedTenantSchema = z.object({
  tenantId: nonEmpty,
  tenantName: nonEmpty,
  role: z.string().optional(),
});

const OpsAccountDeletionRequestedData = z.object({
  templateId: z.literal(EmailTemplateId.OPS_ACCOUNT_DELETION_REQUESTED),
  /** Interno: o alvo do aviso é uma pessoa, não um tenant. */
  userId: nonEmpty,
  userEmail: emailAddr,
  userName: z.string().max(120).optional(),
  deactivatedAt: isoDate,
  /** Onde a pessoa trabalha, com o papel dela — o comercial precisa disso. */
  tenants: z.array(OrphanedTenantSchema),
  /** Subconjunto de `tenants` que ficou SEM dono capaz de entrar. Pode ser vazio. */
  orphanedTenants: z.array(OrphanedTenantSchema),
  /** Quantas pessoas perderam o acesso ao todo, incluindo quem pediu. */
  affectedUsers: z.number().int().nonnegative(),
  reason: z.string().max(400).nullable(),
  /** Painel de ops, onde o bloqueio pode ser desfeito. */
  adminUrl: z.url(),
});

const PrivacyTenantDeleteConfirmationData = z.object({
  templateId: z.literal(EmailTemplateId.PRIVACY_TENANT_DELETE_CONFIRMATION),
  ...baseUserContext.shape,
  ...baseTenantContext.shape,
  /** URL de confirmação OOB (válida por 24h). */
  confirmUrl: z.url(),
  /** Momento exato em que o link expira. */
  expiresAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// ---------------------------------------------------------------------------
// Sprint 5 PR F — paywall (cancelamento self-service + reativação + refund MBG)
// ---------------------------------------------------------------------------

/**
 * Sprint 5 PR F — confirmação ao titular do refund MBG (money-back
 * guarantee) processado automaticamente quando o tenant é encerrado
 * dentro da janela (7 dias mensal / 14 dias anual). O email avisa que
 * o estorno pode levar 5-10 dias úteis para aparecer no extrato.
 */
const BillingMbgRefundIssuedData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_MBG_REFUND_ISSUED),
  ...baseTenantContext.shape,
  /** Valor do refund (sempre full no MBG), em CENTAVOS — o template usa `formatAmount` (#798). */
  amount: z.number().nonnegative(),
  currency: CurrencyShortSchema,
  /** Quando o refund foi solicitado ao gateway (ISO 8601). */
  refundedAt: isoDate,
  /** Janela MBG aplicada — vai no copy ("dentro dos 7 dias..."). */
  windowDays: z.number().int().positive(),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const EmailBillingCycle = z.enum(['monthly', 'quarterly', 'semiannual', 'yearly']);

/**
 * #737 (F5 da #679) — mudança do plano que vale no futuro. `change` diz o
 * quê: aumento de preço de um módulo (aviso de 30 dias, 9.17) ou troca de
 * ciclo. `mandateUrl` só existe em Pix quando o plano novo pede mandato novo —
 * sem autorizá-lo, a mudança não é aplicada.
 */
const BillingPlanChangeNoticeData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_PLAN_CHANGE_NOTICE),
  ...baseTenantContext.shape,
  change: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('PRICE_INCREASE'),
      moduleName: nonEmpty,
      oldAmountCents: z.number().int().nonnegative(),
      newAmountCents: z.number().int().nonnegative(),
    }),
    z.object({
      kind: z.literal('CYCLE_CHANGE'),
      fromCycle: EmailBillingCycle,
    }),
  ]),
  /** Valor recorrente do plano depois da mudança, no ciclo `billingCycle`. */
  newTotalCents: z.number().int().nonnegative(),
  currency: CurrencyShortSchema,
  billingCycle: EmailBillingCycle,
  /** Quando passa a valer (ISO). */
  effectiveAt: nonEmpty,
  mandateUrl: nonEmpty.nullable().default(null),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

/**
 * #737 (F5 da #679) — proporção avulsa de um módulo novo em assinatura Pix.
 * `paymentUrl` VERBATIM da Checkout Session (a URL carrega um fragmento sem o
 * qual não abre). `createsMandate` liga o parágrafo que explica a tela de
 * autorização do banco: o mesmo link cobra a proporção E autoriza o mandato
 * novo quando o total do plano passou do que o mandato atual cobre.
 */
const BillingAddonProrationLinkData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_ADDON_PRORATION_LINK),
  ...baseTenantContext.shape,
  moduleName: nonEmpty,
  prorationCents: z.number().int().positive(),
  /** Valor recorrente do plano a partir do próximo ciclo, com o módulo. */
  newTotalCents: z.number().int().nonnegative(),
  currency: CurrencyShortSchema,
  billingCycle: z.enum(['monthly', 'quarterly', 'semiannual', 'yearly']),
  paymentUrl: nonEmpty,
  /** Até quando pagar (ISO) — depois disso o módulo sai do plano. */
  payUntil: nonEmpty,
  createsMandate: z.boolean().default(false),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

/**
 * Sprint 5 PR F — confirmação ao titular do cancelamento agendado
 * (end-of-period). A subscription segue ACTIVE até `accessUntil` e
 * pode ser reativada antes disso na tela de billing.
 */
const BillingCheckoutAbandonedData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_CHECKOUT_ABANDONED),
  ...baseTenantContext.shape,
  /** Valor que o cliente tentou pagar, em centavos. */
  amountCents: z.number().int().nonnegative(),
  currency: CurrencyShortSchema,
  /**
   * Link para retomar o pagamento. Ou a URL da sessao ainda viva, ou a
   * `after_expiration.recovery.url` (vale 30 dias). VERBATIM — nunca montar a
   * partir do id da sessao: a URL do Checkout carrega um fragmento `#fid...`
   * obrigatorio e sem ele o link nao abre.
   */
  recoveryUrl: nonEmpty,
  /**
   * `true` quando o meio escolhido foi Pix. Liga o paragrafo que explica a
   * autorizacao de debito recorrente — a tela do banco nao diz "pagar R$ X",
   * diz "autorizar Pix Automatico", e o cliente nao reconhece como pagamento.
   */
  wasPix: z.boolean().default(false),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

const BillingSubscriptionCancelledData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_SUBSCRIPTION_CANCELLED),
  ...baseTenantContext.shape,
  /** Quando o cliente solicitou o cancelamento. */
  cancelledAt: isoDate,
  /** Fim do período pago — depois disso a conta perde acesso. */
  accessUntil: isoDate,
  /** Categoria selecionada no flow de retenção. Vai no audit, opcional aqui. */
  reasonCategory: z.string().optional(),
  /**
   * Página do app pra reativar (botão CTA do email). `null` desde a F4 da #679:
   * o cliente não reativa sozinho, e quem cancela é o consultor pelo CRM
   * (#737) — o e-mail diz para falar com ele em vez de mostrar o botão.
   */
  reactivateUrl: z.url().nullable().default(null),
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

/**
 * Sprint 5 PR F — confirmação que o cancelamento agendado foi revertido
 * pelo OWNER. A cobrança recorrente segue normal.
 */
const BillingSubscriptionReactivatedData = z.object({
  templateId: z.literal(EmailTemplateId.BILLING_SUBSCRIPTION_REACTIVATED),
  ...baseTenantContext.shape,
  /** Quando o OWNER reativou. */
  reactivatedAt: isoDate,
  /** Fim do ciclo atual — não muda em reativação. */
  nextChargeAt: isoDate,
  supportEmail: emailAddr.default('suporte@etiquetachef.com'),
});

// BI alerts críticos (Sprint 4.7) — disparados para super-admins quando
// `severity: HIGH` na detecção. Sem tenant context — alertas são globais.
const BiAlertHighData = z.object({
  templateId: z.literal(EmailTemplateId.BI_ALERT_HIGH),
  alertKind: AlertKindSchema,
  severity: AlertSeveritySchema,
  magnitude: z.number(),
  detail: nonEmpty,
  observedAt: isoDate,
  periodFromYyyymmdd: nonEmpty,
  periodToYyyymmdd: nonEmpty,
  dashboardUrl: z.url(),
});

// Alerta interno (super-admins) do monitor de saúde do `daily_ops`
// (`monitorDailyOpsHealth`). Sem tenant context no header — é um resumo
// global. `lines` traz uma linha humana por tenant afetado (ids + números),
// pré-formatada no servidor (não é prosa traduzível).
const AlertDailyOpsHealthData = z.object({
  templateId: z.literal(EmailTemplateId.ALERT_DAILY_OPS_HEALTH),
  /** Data BRT em que o monitor rodou (`yyyy-MM-dd`). */
  runDate: nonEmpty,
  /** Quantidade de tenants com divergência. */
  affectedCount: z.number().int().nonnegative(),
  /** Total de tenants verificados na execução. */
  totalChecked: z.number().int().nonnegative(),
  /** Resumo por tenant afetado (ex.: "IT-… (2026-06-05): consumed esperado=9 atual=0"). */
  lines: z.array(nonEmpty),
});

// Notificação de operação para super-admins (apps/admin). Template genérico:
// `title` (headline + base do subject) e `lines` (corpo) são pré-formatados
// pelo trigger no servidor — não são prosa traduzível, como
// `AlertDailyOpsHealthData.lines`. `event` indica o evento de negócio (para
// telemetria/ícone). CTA opcional aponta para o console admin.
const AdminOpsNotificationData = z.object({
  templateId: z.literal(EmailTemplateId.ADMIN_OPS_NOTIFICATION),
  event: AdminNotificationEventSchema,
  title: nonEmpty,
  lines: z.array(nonEmpty),
  ctaUrl: z.url().optional(),
  ctaLabel: z.string().optional(),
});

// Relatório interno diário de tenants pagantes sem impressão nos últimos 3
// dias civis (`dailyInactiveTenantsReport`). Diferente do
// `AlertDailyOpsHealthData`, as linhas NÃO vêm coladas em string: cada tenant
// é um objeto tipado para o template montar uma tabela de verdade (nome,
// contato do OWNER, plano) em vez de um parágrafo. Destinatário é uma caixa
// interna, então os textos fixos do template são só pt-BR.
export const InactiveTenantRowSchema = z.object({
  tenantId: nonEmpty,
  tenantName: nonEmpty,
  /** `TenantDisplayStatus` já traduzido para exibição ("Ativo"/"Pagamento pendente"). */
  statusLabel: nonEmpty,
  /**
   * Dias civis consecutivos sem imprimir, contados até D-1 no fuso do tenant.
   * `null` quando o tenant nunca imprimiu nenhuma etiqueta (não há "desde
   * quando" — é falha de ativação, não abandono).
   */
  daysWithoutPrinting: z.number().int().positive().nullable(),
  neverPrinted: z.boolean(),
  ownerName: z.string(),
  ownerEmail: z.string(),
  ownerPhone: z.string(),
  /** `yyyy-MM-dd` do início da assinatura; vazio se desconhecido. */
  customerSince: z.string(),
  planLabel: z.string(),
});
export type InactiveTenantRow = z.infer<typeof InactiveTenantRowSchema>;

const ReportInactiveTenantsData = z.object({
  templateId: z.literal(EmailTemplateId.REPORT_INACTIVE_TENANTS),
  /** Data BRT em que o relatório rodou (`yyyy-MM-dd`). */
  runDate: nonEmpty,
  /** Tenants considerados (ativos, não-demo) na execução. */
  totalChecked: z.number().int().nonnegative(),
  /** Total de inativos encontrados — pode ser maior que `rows.length`. */
  inactiveCount: z.number().int().nonnegative(),
  /** Linhas exibidas no e-mail (truncadas em `MAX_EMAIL_ROWS`). */
  rows: z.array(InactiveTenantRowSchema),
  /** Quantos inativos ficaram de fora por truncamento (0 = nenhum). */
  omittedCount: z.number().int().nonnegative(),
  /** Path do doc com a lista completa, citado no rodapé quando há truncamento. */
  reportDocPath: nonEmpty,
});

// Resumo interno diário do monitor de padrão de uso (#886,
// `detectTenantUsageAnomalies`). Uma linha por desvio NOVO do dia — o detector
// só inclui o que acabou de criar em `usage_alerts`, então uma reexecução não
// reenvia linha já avisada. Números crus (etiquetas), formatação no template.
export const UsageAnomalyRowSchema = z.object({
  tenantId: nonEmpty,
  tenantName: nonEmpty,
  /** `TenantDisplayStatus` já traduzido ("Ativo", "Cortesia", …). */
  statusLabel: nonEmpty,
  kind: UsageAlertKindSchema,
  /** `yyyy-MM-dd` no fuso do tenant. */
  periodStartYmd: nonEmpty,
  periodEndYmd: nonEmpty,
  /** Semanal: etiquetas da semana. Habitual: 0. */
  observed: z.number().nonnegative(),
  /** Semanal: mediana das semanas anteriores. Habitual: ocorrências com impressão. */
  baseline: z.number().nonnegative(),
  ratio: z.number().nonnegative(),
  /** 0 = domingo … 6 = sábado; só no habitual. */
  weekday: z.number().int().min(0).max(6).nullable(),
  /** Ocorrências avaliadas no habitual (denominador de `baseline`). */
  lookbackWeeks: z.number().int().positive(),
});
export type UsageAnomalyRow = z.infer<typeof UsageAnomalyRowSchema>;

const ReportUsageAnomaliesData = z.object({
  templateId: z.literal(EmailTemplateId.REPORT_USAGE_ANOMALIES),
  /** Data BRT da execução (`yyyy-MM-dd`). */
  runDate: nonEmpty,
  /** Tenants avaliados. */
  totalChecked: z.number().int().nonnegative(),
  rows: z.array(UsageAnomalyRowSchema),
  /** Linhas cortadas por truncamento (0 = nenhuma). */
  omittedCount: z.number().int().nonnegative(),
  /** Se vendedor/gerente também foram avisados (`config/usageAlerts.notifyCrm`). */
  notifyCrm: z.boolean(),
});

// Relatório interno diário de memória por Cloud Function (#864,
// `dailyMemoryReport`). Utilizações são frações do limite configurado
// (0,85 = 85 % do `memory` da função), exatamente como a métrica
// `run.googleapis.com/container/memory/utilizations` as entrega. `null`
// significa "a fonte não respondeu" — NUNCA zero: um OOM omitido por falta de
// permissão não pode aparecer como "nenhum OOM".
export const MemoryReportRowSchema = z.object({
  /** Nome do serviço Cloud Run (= nome da Function em minúsculas). */
  service: nonEmpty,
  /** `memory` configurado em MiB; `null` se o serviço não veio da Cloud Run API. */
  limitMiB: z.number().int().positive().nullable(),
  /**
   * p50 da utilização nas últimas 24 h. `null` quando o serviço só entrou na
   * lista por ter morrido por OOM sem reportar amostra (auditoria
   * 2026-10-08, #864 A1): antes saía 0, o que puxava a mediana para baixo e
   * dizia "uso zero" de um container que morreu por falta de memória.
   */
  p50: z.number().nonnegative().nullable(),
  /**
   * Maior p99 HORÁRIO das últimas 24 h — o pico que a policy de memória vê
   * (0,92 desde 2026-10-09, era 0,85 — #865; auditoria #864 A5: o p99 do dia
   * inteiro escondia o pico). `null` como o p50.
   */
  p99: z.number().nonnegative().nullable(),
  /** Maior p99 horário dos últimos 7 dias; `null` sem histórico. */
  p99Max7d: z.number().nonnegative().nullable(),
  /** p50 de hoje menos o p50 de 7 dias atrás; `null` sem o ponto de 7 dias atrás. */
  p50Delta7d: z.number().nullable(),
  /** Mortes por OOM nas últimas 24 h; `null` se o Logging não respondeu. */
  oomCount24h: z.number().int().nonnegative().nullable(),
});
export type MemoryReportRow = z.infer<typeof MemoryReportRowSchema>;

const InfraMemoryReportData = z.object({
  templateId: z.literal(EmailTemplateId.INFRA_MEMORY_REPORT),
  /** Data (Europe/Rome) em que o relatório rodou (`yyyy-MM-dd`). */
  runDate: nonEmpty,
  /** Serviços com amostra de memória nas últimas 24 h. */
  serviceCount: z.number().int().nonnegative(),
  /** Serviços listados na Cloud Run API sem amostra em 24 h (sem tráfego). */
  idleServiceCount: z.number().int().nonnegative().nullable(),
  /**
   * Mediana do p50 entre os serviços — a "linha de base" do bundle. `null`
   * quando nenhum serviço teve amostra (#939: antes saía 0, e ausente nunca é
   * zero).
   */
  medianP50: z.number().nonnegative().nullable(),
  /** A mesma mediana 7 dias atrás; `null` sem histórico. */
  medianP50SevenDaysAgo: z.number().nonnegative().nullable(),
  countAtOrAbove85: z.number().int().nonnegative(),
  countAtOrAbove75: z.number().int().nonnegative(),
  /** OOMs em 24 h no projeto todo; `null` se o Logging não respondeu. */
  oomTotal24h: z.number().int().nonnegative().nullable(),
  /** Linhas ordenadas por p99 desc, truncadas em `MAX_EMAIL_ROWS`. */
  rows: z.array(MemoryReportRowSchema),
  omittedCount: z.number().int().nonnegative(),
  /** Fontes que falharam nesta execução (ex.: "Cloud Logging (OOMs)"). */
  unavailableSources: z.array(nonEmpty),
  dashboardUrl: z.url(),
});

// ---------------------------------------------------------------------------
// #664 — Relatórios do módulo `MOD_REPORTS` (e-mail de CLIENTE, 5 locales)
// ---------------------------------------------------------------------------
//
// O corpo leva as seções `inEmail` do documento, com as tabelas cortadas em
// `emailRowLimit` ("e mais N"); o relatório completo vai em PDF anexado
// (`pdfFilename`). O texto do documento já chega no idioma do destinatário —
// o template traduz só a moldura (#664 §9).

const ReportOperationsData = z.object({
  templateId: z.literal(EmailTemplateId.REPORT_OPERATIONS),
  tenantId: nonEmpty,
  document: ReportDocumentSchema,
  /** Nome do anexo, citado no corpo ("o relatório completo está em …"). */
  pdfFilename: nonEmpty,
  appUrl: z.url(),
});

// ---------------------------------------------------------------------------
// Discriminated union — todos os templates suportados
// ---------------------------------------------------------------------------

export const EmailTemplateDataSchema = z.discriminatedUnion('templateId', [
  ReportInactiveTenantsData,
  ReportUsageAnomaliesData,
  InfraMemoryReportData,
  ReportOperationsData,
  OnboardingWelcomeData,
  OnboardingTenantInviteData,
  SaleWelcomeData,
  OnboardingEquipmentData,
  EmailOtpCodeData,
  InvoiceCreatedData,
  InvoiceDueSoonData,
  InvoiceOverdueData,
  PaymentSucceededData,
  PaymentFailedData,
  PaymentActionRequiredData,
  BillingSuspendedData,
  BillingReactivatedData,
  AccountEmailChangedData,
  AccountPasswordChangedData,
  AccountNewDeviceLoginData,
  OpsTenantUserInvitedData,
  OpsSuspectedDataResetData,
  BiAlertHighData,
  LifecycleTenantDeletedData,
  LifecycleTenantRestoredData,
  PrivacyDataExportReadyData,
  PrivacyAccountDeletionRequestedData,
  PrivacyAccountRestoredData,
  PrivacyTenantDeleteConfirmationData,
  AccountDeactivatedData,
  OpsAccountDeletionRequestedData,
  // Sprint 5 PR F
  BillingMbgRefundIssuedData,
  BillingCheckoutAbandonedData,
  BillingAddonProrationLinkData,
  BillingPlanChangeNoticeData,
  BillingSubscriptionCancelledData,
  BillingSubscriptionReactivatedData,
  AlertDailyOpsHealthData,
  AdminOpsNotificationData,
]);
export type EmailTemplateData = z.infer<typeof EmailTemplateDataSchema>;

// `SendTransactionalEmailInputSchema`/`OutputSchema` REMOVIDOS (auditoria
// §3.2.1): serviam só o callable `sendTransactionalEmail`, removido por ser
// open relay. Envio interno usa `sendgridSendEmail`/`sendInternalEmail` com
// os schemas de template acima (`EmailTemplateDataSchema`). Reintroduzir só
// se um callable de e-mail GATEADO voltar a ser necessário.

/**
 * Schema do payload do SendGrid Event Webhook. SendGrid posta um array
 * de eventos por requisição — cada item carrega `email`, `event`,
 * `sg_message_id`, `timestamp`, e os `custom_args` (`idempotencyKey`,
 * `templateId`, `locale`, opcionalmente `tenantId`) que injetamos no
 * envio. O schema abaixo aceita campos extras (passthrough) porque
 * SendGrid evolui o payload e não queremos quebrar com campo novo.
 */
export const SendGridEventSchema = z.looseObject({
  email: emailAddr,
  event: z.enum(ALL_EMAIL_EVENT_TYPES),
  sg_message_id: nonEmpty,
  sg_event_id: nonEmpty,
  timestamp: z.number().int().nonnegative(),
  reason: z.string().optional(),
  type: z.string().optional(),
  idempotencyKey: nonEmpty.optional(),
  templateId: nonEmpty.optional(),
  locale: nonEmpty.optional(),
  tenantId: nonEmpty.optional(),
});
export type SendGridEvent = z.infer<typeof SendGridEventSchema>;

export const SendGridEventBatchSchema = z.array(SendGridEventSchema);
